/**
 * colorUtils — lightweight color manipulation for CSS custom property generation.
 *
 * Pure functions, no dependencies. Used by useThemeVars to derive
 * tint variants from project theme colors.
 *
 * Only HEX input is supported (3, 6 or 8 digit). Returns CSS hsl() strings
 * so browsers handle clamping and gamut mapping natively.
 */

// ── HELPERS ─────────────────────────────────────────────────────────────────

export function isValidCssColor(value) {
  if (typeof value !== 'string') return false
  const v = value.trim()
  // HEX
  if (/^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(v)) return true
  // rgb(a)
  if (/^rgb(a)?\(/i.test(v)) return true
  // hsl(a)
  if (/^hsl(a)?\(/i.test(v)) return true
  // CSS var
  if (/^var\(/.test(v)) return true
  return false
}

export function normalizeCssColorToken(value) {
  if (typeof value !== 'string') return null
  const normalized = value.trim()
  if (!normalized) return null
  if (
    normalized.startsWith('#') ||
    normalized.startsWith('var(') ||
    normalized.startsWith('rgb(') ||
    normalized.startsWith('rgba(') ||
    normalized.startsWith('hsl(') ||
    normalized.startsWith('hsla(')
  ) {
    return normalized
  }
  return `#${normalized}`
}

/**
 * Clamps a number to [min, max].
 *
 * @param {number} value
 * @param {number} [min=0]
 * @param {number} [max=100]
 * @returns {number}
 */
export function clamp(value, min = 0, max = 100) {
  return Math.max(min, Math.min(max, value))
}


// ── COLOR STRING CONVERTERS ─────────────────────────────────────────────────────────────────

// ── HEX → RGBA ─────────────────────────────────────────────────────────────────
/**
 * Parses a CSS hex color string (#rgb / #rrggbb / #rrggbbaa) to { r, g, b, a } (0–255, 0–1).
 * Returns null for any invalid input — callers must guard.
 *
 * @param {string} hex
 * @param {number} [alphaOverride]
 * @returns {{ r: number, g: number, b: number, a: number } | null}
 */
export function hexToRgba(hex, alphaOverride) {
  if (typeof hex !== 'string') return null
  const raw = hex.trim().replace(/^#/, '')
  let r, g, b, a = 1

  if (raw.length === 3) {
    r = parseInt(raw[0] + raw[0], 16)
    g = parseInt(raw[1] + raw[1], 16)
    b = parseInt(raw[2] + raw[2], 16)
  } else if (raw.length === 4) {
    r = parseInt(raw[0] + raw[0], 16)
    g = parseInt(raw[1] + raw[1], 16)
    b = parseInt(raw[2] + raw[2], 16)
    a = parseInt(raw[3] + raw[3], 16) / 255
  } else if (raw.length === 6) {
    r = parseInt(raw.slice(0, 2), 16)
    g = parseInt(raw.slice(2, 4), 16)
    b = parseInt(raw.slice(4, 6), 16)
  } else if (raw.length === 8) {
    r = parseInt(raw.slice(0, 2), 16)
    g = parseInt(raw.slice(2, 4), 16)
    b = parseInt(raw.slice(4, 6), 16)
    a = parseInt(raw.slice(6, 8), 16) / 255
  } else {
    return null
  }

  if (isNaN(r) || isNaN(g) || isNaN(b)) return null
  if (typeof alphaOverride === 'number') a = alphaOverride
  return { r, g, b, a }
}


// ── RGB → HSL ─────────────────────────────────────────────────────────────────
/**
 * Converts { r, g, b } (0–255) to { h (0–360), s (0–100), l (0–100) }.
 *
 * @param {{ r: number, g: number, b: number }} rgb
 * @returns {{ h: number, s: number, l: number }}
 */
export function rgbToHsl({ r, g, b }) {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255

  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const delta = max - min

  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1))

    if (max === rn)      h = ((gn - bn) / delta) % 6
    else if (max === gn) h = (bn - rn) / delta + 2
    else                 h = (rn - gn) / delta + 4

    h = h * 60
    if (h < 0) h += 360
  }

  return { h, s: s * 100, l: l * 100 }
}

// ── HEX → HSL ─────────────────────────────────────────────────────────────────
/**
 * Parses a hex string directly to HSL. Returns null if input is invalid.
 *
 * @param {string} hex
 * @returns {{ h: number, s: number, l: number } | null}
 */
export function hexToHsl(hex) {
  const rgba = hexToRgba(hex)
  if (!rgba) return null
  const { r, g, b } = rgba
  return rgbToHsl({ r, g, b })
}


// ── COLOR STRING MODIFIERS ─────────────────────────────────────────────────────────────────

/**
 * Adjusts HSL values and returns a CSS `hsl()` string.
 *
 * @param {{ h: number, s: number, l: number }} hsl
 * @param {{ h?: number, s?: number, l?: number }} delta  - amounts to add (can be negative)
 * @returns {string}  e.g. "hsl(38, 55%, 85%)"
 */
export function adjustHsl({ h, s, l }, { h: dh = 0, s: ds = 0, l: dl = 0 }) {
  const hn = ((h + dh) % 360 + 360) % 360
  const sn = clamp(s + ds)
  const ln = clamp(l + dl)
  return `hsl(${hn.toFixed(0)}, ${sn.toFixed(0)}%, ${ln.toFixed(0)}%)`
}

/**
 * Converts [r, g, b] (0–255, may be float) to a 6-digit hex string.
 *
 * @param {number} r
 * @param {number} g
 * @param {number} b
 * @returns {string}
 */
function _rgbToHex(r, g, b) {
  return '#' + [r, g, b].map((c) => Math.round(Math.max(0, Math.min(255, c))).toString(16).padStart(2, '0')).join('')
}

/**
 * Linearly interpolates between two hex colors and returns a hex string.
 * t=0 → colorA, t=1 → colorB.
 *
 * Returns a fallback gray '#cccccc' if either input is falsy or unparseable.
 *
 * @param {string} colorA — 3 or 6 digit hex
 * @param {string} colorB — 3 or 6 digit hex
 * @param {number} t      — interpolation factor 0–1
 * @returns {string}
 */
export function mixHex(colorA, colorB, t) {
  const a = hexToRgba(colorA)
  const b = hexToRgba(colorB)
  if (!a || !b) return (colorA ?? colorB) ?? '#cccccc'
  return _rgbToHex(
    a.r + (b.r - a.r) * t,
    a.g + (b.g - a.g) * t,
    a.b + (b.b - a.b) * t,
  )
}

/**
 * Adds alpha to HEX values and returns as CSS `rgba()` string.
 * 
 * @param {string} hex 
 * @param {number} alpha 
 * @returns {string|null} e.g. "rgba(255, 0, 0, 0.5)" or null if input is invalid
 */
export function adjustAsRgba(hex, alpha = 1) {
  const rgba = hexToRgba(hex, alpha)
  if (!rgba) return null
  const { r, g, b, a } = rgba
  return `rgba(${r},${g},${b},${a})`
}

/**
 * Returns the best contrast color (black or white) for the given HEX color.
 *
 * @param {string} hex
 * @returns {string} "#000" or "#fff"
 */
export function getBestContrast(hex) {
  const rgba = hexToRgba(hex)
  if (!rgba) return '#fff'
  // luminancja
  const lum = (0.299 * rgba.r + 0.587 * rgba.g + 0.114 * rgba.b) / 255
  return lum > 0.5 ? '#000' : '#fff'
}

/**
 * Returns a muted contrast color: a medium gray that contrasts with the input color white or black
 *
 * @param {string} hex
 * @returns {string} "#333" or "#dedede"
 */
export function getMutedContrast(hex) {
  const on = getBestContrast(hex)
  return on === '#fff' ? '#dedede' : '#333'
}


/**
 * 
 * @param {string} baseColor hex
 * @param { string } type variant type: 'base' | 'shade' | 'tint' | 'alpha' | 'contrast' | 'muted'
 * @param {*} params additional parameters depending on type:
 *   - shade/tint: { percent: number } (positive to lighten, negative to darken)
 *   - alpha: { alpha: number } (0–1)
 * @returns {string|null} CSS color string or null if input is invalid
 */
export function autoDeriveVariant(baseColor, type, params = {}) {
  const hsl = hexToHsl(baseColor)
  if (!hsl) return null

  switch (type) {
    case 'base':
      return baseColor
    case 'shade':
      return adjustHsl(hsl, { l: -params.percent })
    case 'tint':
      return adjustHsl(hsl, { l: +params.percent })
    case 'alpha':
      return adjustAsRgba(baseColor, params.alpha)
    case 'contrast':
      return getBestContrast(baseColor)
    case 'muted':
      return getMutedContrast(baseColor)
    default:
      return null
  }
}